@if (isCard() && compact()) {
  <!-- A part of a group discloses its body under the headline, with no box. -->
  @if (collapsible()) {
    <ng-container [ngTemplateOutlet]="disclosure" />
  } @else {
    <div class="lp-verlauf-timeline-entry__body dm-min-w-0">
      <ng-container [ngTemplateOutlet]="headline" />
      <ng-container [ngTemplateOutlet]="cardBody" />
    </div>
  }
} @else {
  @if (!compact()) {
    <div class="dm-flex dm-flex-column dm-items-center dm-gap-2">
      <ng-container [ngTemplateOutlet]="gutterMarker" />
      @if (!isLast()) {
        <span class="lp-verlauf-timeline-entry__connector"></span>
      }
    </div>
  }

  <div class="lp-verlauf-timeline-entry__body dm-min-w-0">
    @if (isCard() && collapsible()) {
      <ng-container [ngTemplateOutlet]="disclosure" />
    } @else {
      <div class="dm-flex dm-items-start dm-justify-between dm-gap-8">
        <ng-container [ngTemplateOutlet]="headline" />
        <div class="lp-verlauf-timeline-entry__actions dm-flex dm-flex-none dm-items-center dm-gap-4">
          <ng-container [ngTemplateOutlet]="trailingSlot" />
          <ng-container [ngTemplateOutlet]="menuSlot" />
        </div>
      </div>
      <ng-content select="[subline]" />
      @if (isCard()) {
        <ng-container [ngTemplateOutlet]="box" />
      }
    }
  </div>
}

<!-- One line saying who did what, and when. -->
<ng-template #headline>
  <div class="lp-verlauf-timeline-entry__headline lp-dot-separated dm-flex dm-flex-wrap dm-items-center dm-gap-6 dm-min-w-0">
    <!-- Inside a group the gutter belongs to the group, so the icon leads the line. -->
    @if (compact() && iconName(); as icon) {
      <dm-icon class="dm-color-text-tertiary" size="sm" [name]="icon" />
    }
    <ng-container [ngTemplateOutlet]="authorLead" />
    <span class="dm-text-sm dm-color-text-secondary">{{ entry().headline }}</span>
    <ng-container [ngTemplateOutlet]="titleExtraSlot" />
    <ng-container [ngTemplateOutlet]="timestamp" />
  </div>
</ng-template>

<!-- Who did it. The avatar appears only where the gutter is already an icon, so a
     row never carries two markers, and never none. -->
<ng-template #authorLead>
  @if (showsAuthorAvatar()) {
    <dm-avatar size="xs" [lpAvatarFromPerson]="entry().author" />
  }
  @if (showsAuthorName()) {
    <span class="dm-text-sm dm-text-weight-medium dm-color-text-primary">{{ authorName() }}</span>
  }
</ng-template>

<!-- When, closing the same line: the headline's size, one step back in colour. Its «·» shows only
     while it shares a line with what precedes it. -->
<ng-template #timestamp>
  @if (timestampLabel(); as label) {
    <span class="lp-dot-separated__part dm-text-sm dm-color-text-quaternary">{{ label }}</span>
  }
</ng-template>

<!-- A body folded away behind the library's single-item accordion, whose trigger is the line. -->
<ng-template #disclosure>
  <dm-accordion class="lp-verlauf-timeline-entry__accordion" [multiple]="true" [collapsible]="true">
    <dm-accordion-item>
      <dm-accordion-trigger>
        <ng-container [ngTemplateOutlet]="headline" />
      </dm-accordion-trigger>
      <dm-accordion-content>
        @if (compact()) {
          <div class="dm-padding-bottom-16">
            <ng-container [ngTemplateOutlet]="cardBody" />
          </div>
          <!-- Inside the disclosure, so it folds with the body. -->
          <div class="lp-verlauf-timeline-entry__footer">
            <ng-container [ngTemplateOutlet]="footerSlot" />
          </div>
        } @else {
          <ng-container [ngTemplateOutlet]="box" />
        }
      </dm-accordion-content>
    </dm-accordion-item>
  </dm-accordion>
</ng-template>

<!-- The body, boxed beneath the line that says who and when; the box holds content only. -->
<ng-template #box>
  @if (isEditing()) {
    <!-- An editor draws its own border, so it stands in for the box. -->
    <ng-container [ngTemplateOutlet]="cardBody" />
  } @else if (hint() && !collapsible()) {
    <dm-tooltip anchor="block" placement="top-start" [arrow]="true" [label]="hint()">
      <dm-card class="lp-verlauf-timeline-entry__box" tabindex="0">
        <ng-container [ngTemplateOutlet]="boxContent" />
      </dm-card>
    </dm-tooltip>
  } @else {
    <dm-card class="lp-verlauf-timeline-entry__box">
      <ng-container [ngTemplateOutlet]="boxContent" />
    </dm-card>
  }
</ng-template>

<ng-template #boxContent>
  @if (!collapsible() && link(); as linkUrl) {
    <!-- A direct child of dm-card, which stretches it over the box. -->
    <a [routerLink]="linkUrl" [attr.aria-label]="linkLabel()"></a>
  }
  <dm-card-body>
    <ng-container [ngTemplateOutlet]="cardBody" />
  </dm-card-body>
  <!-- Outside the body's inset, so the band reaches the box's edges. -->
  <div class="lp-verlauf-timeline-entry__footer">
    <ng-container [ngTemplateOutlet]="footerSlot" />
  </div>
</ng-template>

<!-- What happened, in the marker column; an entry naming no kind shows its author's avatar. -->
<ng-template #gutterMarker>
  @if (iconName(); as icon) {
    <dm-icon class="lp-verlauf-timeline-entry__icon" size="sm" theme="modern" color="gray" [featured]="true" [name]="icon" />
  } @else {
    <dm-avatar size="sm" [lpAvatarFromPerson]="entry().author" />
  }
</ng-template>

<!-- Both slots reach two shapes of the entry, and a second `ng-content` for the
     same selector would leave one of them empty — so each is declared once. -->
<ng-template #titleExtraSlot>
  <ng-content select="[title-extra]" />
</ng-template>

<ng-template #trailingSlot>
  <ng-content select="[trailing]" />
</ng-template>

<ng-template #menuSlot>
  <ng-content select="[menu]" />
</ng-template>

<ng-template #footerSlot>
  <ng-content select="[footer]" />
</ng-template>

<!-- Declared once and rendered into whichever shape the body takes: a second
     `ng-content` for the same slot would leave one of the two empty. -->
<ng-template #cardBody>
  <div class="lp-verlauf-timeline-entry__content">
    <ng-content />
  </div>
</ng-template>
